<template>
    <div v-if="effect" class="effects-body">
        <div class="provider-select">
            <c-lazy>
                <img v-if="effect.thumbnail" :src="effect.thumbnail" :alt="effect.label" class="thumbnail-image" lazy />
                <img
                    v-else
                    :src="require('@/angular-app/images/placeholderCustomily.png')"
                    :alt="effect.label"
                    class="thumbnail-image"
                    lazy
                />
            </c-lazy>
            <template v-if="effect.presets">
                <div class="provider-select-input">
                    <label>Service Provider</label>
                    <select v-model="provider" class="form-control">
                        <option
                            v-for="option in effect.presets"
                            :key="option.id"
                            :value="{ type: option?.effectConfig?.type || option?.id, id: option?.id }"
                        >
                            {{ option.tags[0] || option.label }}
                        </option>
                    </select>
                </div>
            </template>
            <template v-else>
                <div class="provider-selected-label">
                    {{ effect?.label }} <span v-if="effect.tags">({{ effect.tags[0] }})</span>
                </div>
            </template>
        </div>
        <div v-if="provider">
            <form ref="formRef" class="space-y-4" @submit.prevent="handleSave">
                <component
                    :is="currentEffectComponent"
                    v-if="currentEffectComponent"
                    :image-effect="formData"
                    @update:image-effect="formData = $event"
                />
            </form>
        </div>
        <api-key-requirement-footer
            :preset="selectedPreset"
            v-bind="props.effect && props.effect.isFree !== undefined ? { isFree: props.effect.isFree } : {}"
        />
        <div class="d-flex justify-content-between custom-footer">
            <c-button :text="effect.presets ? 'Back' : 'Close'" variant="secondary" @click="handleClose" />
            <c-button text="Apply Effect" variant="primary" @click="submitForm" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import CButton from '@/components/shared/CButton';
import CLazy from '@/components/CLazy.vue';
import store from '@/store';
import { getEffectComponent } from '@/components/DesignStudio/ImageEffectsProperties/utils/componentMapper';
import { EffectType, ImageEffectType } from '@/typings/customilyElements/imageEffectType';
import { CombinedPresets, PresetTag, Provider } from '@/components/DesignStudio/ImageEffectsProperties/types/presets';
import ApiKeyRequirementFooter from './ApiKeyRequirementFooter/ApiKeyRequirementFooter.vue';

const formRef = ref<HTMLFormElement | null>(null);
const formData = ref<ImageEffectType | undefined>(undefined);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const currentEffectComponent = ref<any>(null);

const emit = defineEmits<{
    (event: 'update', value: ImageEffectType, provider?: string): void;
}>();

interface Props {
    effect: CombinedPresets | null;
}
const props = withDefaults(defineProps<Props>(), {
    effect: null
});

const provider = ref<Provider | null>(null);

const selectedPreset = computed(() => {
    if (!props.effect?.presets) return;
    return props.effect.presets.find((preset) => preset.effectConfig.presetId === provider.value?.id);
});

// A form whose import resolves after the provider changed would mount on the new provider's data and emit into it.
const loadEffectComponent = async (effectType: EffectType) => {
    try {
        const component = await getEffectComponent(effectType);
        if (provider.value?.type === effectType) currentEffectComponent.value = component;
    } catch (error) {
        console.error('Error loading effect component:', error);
        if (provider.value?.type === effectType) currentEffectComponent.value = null;
    }
};

const submitForm = () => {
    // Only `required` blocks saving; other native constraints (e.g. min/max) keep their previous behavior.
    const missing = formRef.value?.querySelector<HTMLInputElement | HTMLTextAreaElement>('[required]:invalid');
    if (missing) {
        missing.reportValidity();
        return;
    }
    formRef.value?.dispatchEvent(new Event('submit', { cancelable: true }));
};

const resetForm = () => {
    currentEffectComponent.value = null;
    store.dispatch('imageEffectsProperties/resetModalState');
};

const handleClose = () => {
    resetForm();
    if (!props.effect?.presets) {
        store.dispatch('imageEffectsProperties/closeAddModal');
    }
};

const handleSave = () => {
    if (formData.value && provider.value) emit('update', formData.value, provider.value?.id);
};

const setDefaultValuesToForm = () => {
    formData.value = undefined;
    if (!selectedPreset.value) return;
    const { type, presetId, ...defaultFormValues } = selectedPreset.value.effectConfig;
    formData.value = defaultFormValues as ImageEffectType;
};

watch(
    () => provider.value,
    () => {
        if (!provider.value?.id && props.effect?.presets) return;
        if (props.effect?.presets) {
            setDefaultValuesToForm();
        }
        if (provider.value) loadEffectComponent(provider.value.type);
    },
    { deep: true, immediate: true }
);

watch(
    () => props.effect,
    () => {
        if (props.effect?.type) provider.value = { type: props.effect.type };
        if (props.effect?.formData) {
            formData.value = props.effect.formData;
        }
        if (props.effect?.presets?.length) {
            let preset;
            if (props.effect?.presets?.length === 1) {
                preset = props.effect?.presets[0];
            } else {
                preset =
                    props.effect?.presets.find((presetOption) => presetOption.tags.includes(PresetTag.CUSTOMILY)) ||
                    props.effect?.presets[0];
            }
            if (preset) provider.value = { type: preset.effectConfig.type, id: preset.id as EffectType };
            if (props.effect?.presets?.length === 1 && props.effect?.presets[0].tags.includes(PresetTag.CUSTOMILY)) {
                setDefaultValuesToForm();
                handleSave();
            }
        }
    },
    { deep: true, immediate: true }
);
</script>

<style scoped lang="scss">
.effects-body {
    display: flex;
    flex-direction: column;
    gap: 16px;

    .provider-select {
        display: flex;
    }
}

.thumbnail-image {
    width: 6rem;
    height: 6rem;
    border-radius: 5px;
}
.provider-select-input {
    width: 100%;
    margin-left: 1rem;
    display: flex;
    flex-direction: column;
    justify-content: center;

    label {
        margin-bottom: 0;
        color: grey;
        font-weight: 500;
    }
}

.provider-selected-label {
    margin-left: 2rem;
    font-weight: 600;
}

.custom-footer {
    border-top: 1px solid #dee2e6;
    position: relative;
    bottom: 0;
    left: -16px;
    width: calc(100% + 32px);
    padding: 1rem 1rem 0 1rem;
}
</style>
